import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Download, Eye, Info, Pause, Play, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { deleteApiCall, getApiCall, patchApiCall, postApiCall } from '@/service/httpCall';
import { API_CONTACT_GROUP_LIST, API_VOICE_CAMPAIGNS_BASE, API_VOICE_CAMPAIGNS_BASES, API_VOICE_CAMPAIGNS_BASE_STATUS } from '@/service/url';
import NumbersViewerModal, { downloadBaseFile as download, downloadNumberTemplate } from './NumbersViewerModal';
import { usePersistedState } from '@/lib/filters/persisted-state';

const toastApiError = (fallback: string) => (err: unknown) =>
  toast.error(typeof err === 'string' && err ? err : fallback);

interface CampaignBase {
  base_id: number | null;
  base_name: string;
  total_numbers: number | null;
  duplicate_numbers: number | null;
  status: number | null;
  upload_date: string | null;
  contact_group_id: number | null;
  contact_group_name?: string | null;
  source_base_id: number | null;
  download_url: string | null;
}

interface BasesResponse {
  bases: CampaignBase[];
  legacy: boolean;
  total: number;
  live_total: number;
  from: string | null;
  to: string | null;
  total_nos: number;
  editable_now: boolean;
  can_change_status: boolean;
  can_delete?: boolean;
  lock_message: string | null;
}

interface ContactGroup {
  id: number;
  group_name: string;
  member_count?: number | string;
}

type AddMethod = 'manual' | 'upload_file' | 'contact_group';

const BASE_NUMBER_CAP = 50000; // backend BASE_NUMBER_CAP (per base)
const VALID_NUMBER = /^[6-9][0-9]{9}$/;

/** Same rule as backend toNumber: strip non-digits, then 10 digits starting 6-9. */
const toNumber = (value: string) => {
  const digits = value.trim().replace(/\D/g, '');
  return VALID_NUMBER.test(digits) ? digits : null;
};

/** Valid numbers in manual text (newline / comma / semicolon separated). */
export const manualNumbers = (text: string) =>
  text.split(/[\n,;]+/).map(toNumber).filter((n): n is string => !!n);

/** First CSV cell of a line (quotes honoured), like backend splitCsvLine(line)[0]. */
const firstCell = (line: string) => {
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cur += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') break;
    else cur += ch;
  }
  return cur;
};

/** Valid numbers in a CSV / TXT file: first column of each line. */
export const fileNumbers = (text: string) =>
  text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => toNumber(firstCell(line)))
    .filter((n): n is string => !!n);

const STATUS_PILL: Record<number, { label: string; tone: string }> = {
  [-1]: { label: 'Failed', tone: 'bg-red-100 text-red-800 ring-red-300 dark:bg-red-900/30 dark:text-red-200 dark:ring-red-800' },
  0: { label: 'New', tone: 'bg-amber-100 text-amber-800 ring-amber-300 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-800' },
  1: { label: 'Running', tone: 'bg-emerald-100 text-emerald-800 ring-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-800' },
  2: { label: 'Paused', tone: 'bg-slate-100 text-slate-700 ring-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600' },
  3: { label: 'Completed', tone: 'bg-sky-100 text-sky-800 ring-sky-300 dark:bg-sky-900/30 dark:text-sky-200 dark:ring-sky-800' },
  4: { label: 'Processing', tone: 'bg-violet-100 text-violet-800 ring-violet-300 dark:bg-violet-900/30 dark:text-violet-200 dark:ring-violet-800' },
};
const statusPill = (status: number | null) =>
  STATUS_PILL[Number(status)] ?? { label: status == null ? '—' : `Status ${status}`, tone: STATUS_PILL[2]!.tone };
const LEGACY_PILL = { label: 'Legacy', tone: 'bg-slate-100 text-slate-700 ring-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600' };

const BASE_RANGE_DAYS = 7;
const DAY_MS = 86400000;
const istToday = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
const shiftDay = (day: string, days: number) => new Date(Date.parse(day) + days * DAY_MS).toISOString().slice(0, 10);
const defaultRange = () => {
  const to = istToday();
  return { from: shiftDay(to, -(BASE_RANGE_DAYS - 1)), to };
};

const fmtDateTime = (value: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const originLabel = (b: CampaignBase) =>
  b.contact_group_id
    ? `Contact group ${b.contact_group_name || `#${b.contact_group_id}`}`
    : b.source_base_id
      ? `Existing base #${b.source_base_id}`
      : b.base_name.startsWith('manual_')
        ? 'Manual input'
        : 'Uploaded file';

export default function CampaignBasesSection({
  campaignId,
  campaignName,
  allowStatusChange = false,
  allowAdd = false,
  onChanged,
  onLoaded,
}: {
  campaignId: number | string;
  campaignName?: string;
  allowStatusChange?: boolean;
  allowAdd?: boolean;
  /** Called after a base is added or deleted (total_nos changed). */
  onChanged?: () => void;
  onLoaded?: (res: { legacy: boolean } | null) => void;
}) {
  const [data, setData] = useState<BasesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyBaseId, setBusyBaseId] = useState<number | null>(null);
  const [detail, setDetail] = useState<CampaignBase | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmBase, setConfirmBase] = useState<CampaignBase | null>(null);
  const [deleteBase, setDeleteBase] = useState<CampaignBase | null>(null);
  // '' = all bases, otherwise the base_id to preselect; null = viewer closed.
  const [viewBaseId, setViewBaseId] = useState<string | null>(null);
  const [range, setRange] = usePersistedState(`campaigns/CampaignBases/${campaignId}:range`, defaultRange);
  const [paging, setPaging] = usePersistedState(`campaigns/CampaignBases/${campaignId}:paging`, { pageIndex: 0, pageSize: 10 });

  const load = useCallback(() => {
    setLoading(true);
    getApiCall(API_VOICE_CAMPAIGNS_BASES.replace(':id', String(campaignId)), {
      from: range.from,
      to: range.to,
      page: paging.pageIndex + 1,
      limit: paging.pageSize,
    })
      .then((res: BasesResponse) => {
        setData(res);
        onLoaded?.(res);
      })
      .catch(() => {
        setData(null);
        onLoaded?.(null);
      })
      .finally(() => setLoading(false));
  }, [campaignId, range, paging]);

  const changeFrom = (from: string) => {
    if (!from) return;
    const today = istToday();
    const f = from > today ? today : from;
    let to = range.to < f ? f : range.to;
    if (to > shiftDay(f, BASE_RANGE_DAYS - 1)) to = shiftDay(f, BASE_RANGE_DAYS - 1);
    if (to > today) to = today;
    setRange({ from: f, to });
    setPaging((p) => ({ ...p, pageIndex: 0 }));
  };
  const changeTo = (to: string) => {
    if (!to) return;
    const today = istToday();
    const t = to > today ? today : to;
    let from = range.from > t ? t : range.from;
    if (from < shiftDay(t, -(BASE_RANGE_DAYS - 1))) from = shiftDay(t, -(BASE_RANGE_DAYS - 1));
    setRange({ from, to: t });
    setPaging((p) => ({ ...p, pageIndex: 0 }));
  };

  useEffect(() => {
    load();
  }, [load]);

  const canToggle = allowStatusChange && !!data?.can_change_status;
  const canDelete = allowStatusChange && !!data?.can_delete;

  const removeBase = useCallback(
    (b: CampaignBase) => {
      setBusyBaseId(b.base_id);
      deleteApiCall(API_VOICE_CAMPAIGNS_BASE.replace(':id', String(campaignId)).replace(':baseId', String(b.base_id)))
        .then(() => {
          toast.success('Base deleted');
          onChanged?.();
        })
        .catch((err: { data?: { message?: string } } | undefined) =>
          toast.error(err?.data?.message || 'Unable to delete base'),
        )
        .finally(() => {
          setBusyBaseId(null);
          setDeleteBase(null);
          load();
        });
    },
    [campaignId, load, onChanged],
  );

  const toggleStatus = useCallback(
    (b: CampaignBase) => {
      const next = Number(b.status) === 2 ? 1 : 2;
      setBusyBaseId(b.base_id);
      patchApiCall(
        API_VOICE_CAMPAIGNS_BASE_STATUS.replace(':id', String(campaignId)).replace(':baseId', String(b.base_id)),
        { status: next },
      )
        .then(() => {
          toast.success(next === 2 ? 'Base paused' : 'Base resumed');
          load();
        })
        .catch((err) => {
          toastApiError('Unable to change base status')(err);
          load();
        })
        .finally(() => {
          setBusyBaseId(null);
          setConfirmBase(null);
        });
    },
    [campaignId, load],
  );

  const legacy = !!data?.legacy;
  const columns = useMemo<ColumnDef<CampaignBase>[]>(
    () => [
      {
        id: 'file',
        header: 'Base',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-medium" title={row.original.base_name}>
              {row.original.base_name}
            </div>
            <div className="text-xs text-muted-foreground">
              {row.original.base_id != null ? `ID ${row.original.base_id} · ` : ''}
              {originLabel(row.original)}
            </div>
          </div>
        ),
      },
      {
        id: 'numbers',
        header: 'Numbers',
        cell: ({ row }) => (
          <div>
            <div>{Number(row.original.total_numbers || 0).toLocaleString('en-IN')}</div>
            {Number(row.original.duplicate_numbers) > 0 && (
              <div className="text-xs text-muted-foreground">
                Duplicates removed: {Number(row.original.duplicate_numbers).toLocaleString('en-IN')}
              </div>
            )}
          </div>
        ),
      },
      { id: 'uploaded', header: 'Uploaded On', cell: ({ row }) => fmtDateTime(row.original.upload_date) },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const pill = legacy ? LEGACY_PILL : statusPill(row.original.status);
          return (
            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${pill.tone}`}>
              {pill.label}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const b = row.original;
          const toggleable = canToggle && [1, 2].includes(Number(b.status));
          return (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" title="Details" onClick={() => setDetail(b)}>
                <Info className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                title="View numbers"
                onClick={() => setViewBaseId(legacy ? '' : String(b.base_id))}
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" title="Download" disabled={!b.download_url} onClick={() => download(b, campaignId)}>
                <Download className="h-4 w-4" />
              </Button>
              {toggleable && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busyBaseId === b.base_id}
                  onClick={() => setConfirmBase(b)}
                >
                  {Number(b.status) === 2 ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                  {Number(b.status) === 2 ? 'Resume' : 'Pause'}
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  title="Delete base"
                  className="text-destructive hover:text-destructive"
                  disabled={busyBaseId === b.base_id}
                  onClick={() => setDeleteBase(b)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [legacy, canToggle, canDelete, busyBaseId],
  );

  const addAllowed = allowAdd && !!data?.editable_now && !legacy;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-muted-foreground">
          Total numbers: <span className="font-medium text-foreground">{Number(data?.total_nos || 0).toLocaleString('en-IN')}</span>
          {legacy
            ? data?.bases.some((b) => b.base_id == null)
              ? ' · older campaign, linked contact groups'
              : ' · older campaign, single contact file'
            : ''}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!Number(data?.total_nos)}
            onClick={() => setViewBaseId('')}
          >
            <Eye className="h-4 w-4" /> View numbers
          </Button>
          {addAllowed && (
            <Button variant="outline" size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="h-4 w-4" /> Add base
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading} title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      {!legacy && (
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>Uploaded from</span>
          <Input
            type="date"
            className="h-8 w-[150px]"
            value={range.from}
            max={istToday()}
            onChange={(e) => changeFrom(e.target.value)}
          />
          <span>to</span>
          <Input
            type="date"
            className="h-8 w-[150px]"
            value={range.to}
            min={range.from}
            max={[shiftDay(range.from, BASE_RANGE_DAYS - 1), istToday()].sort()[0]}
            onChange={(e) => changeTo(e.target.value)}
          />
          <span>(max {BASE_RANGE_DAYS} days)</span>
          {data && Number(data.live_total) > Number(data.total) && (
            <span className="font-medium text-amber-700 dark:text-amber-300">
              {Number(data.live_total) - Number(data.total)} more base
              {Number(data.live_total) - Number(data.total) === 1 ? '' : 's'} uploaded outside this date range
            </span>
          )}
        </div>
      )}
      {allowStatusChange && data?.lock_message && (
        <p className="mb-2 text-xs text-amber-700 dark:text-amber-300">{data.lock_message}</p>
      )}
      <DataTable
        tableId="campaign.uploaded-bases"
        columns={columns}
        data={data?.bases || []}
        isLoading={loading && !data}
        pageSize={10}
        serverPagination={legacy ? undefined : { ...paging, rowCount: Number(data?.total || 0), onChange: setPaging }}
        disableSearch
        disableExport
        getRowId={(b) => (b.base_id != null ? String(b.base_id) : `group-${b.contact_group_id}`)}
        emptyTitle={legacy ? 'No uploaded bases' : 'No bases uploaded in this date range'}
      />

      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Base details</DialogTitle>
          </DialogHeader>
          {detail && (
            <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Base ID</dt>
              <dd>{detail.base_id ?? '—'}</dd>
              <dt className="text-muted-foreground">File name</dt>
              <dd className="break-all">{detail.base_name}</dd>
              <dt className="text-muted-foreground">Source</dt>
              <dd>{originLabel(detail)}</dd>
              <dt className="text-muted-foreground">Numbers</dt>
              <dd>{Number(detail.total_numbers || 0).toLocaleString('en-IN')}</dd>
              <dt className="text-muted-foreground">Duplicates removed</dt>
              <dd>{Number(detail.duplicate_numbers || 0).toLocaleString('en-IN')}</dd>
              <dt className="text-muted-foreground">Status</dt>
              <dd>{legacy ? LEGACY_PILL.label : statusPill(detail.status).label}</dd>
              <dt className="text-muted-foreground">Uploaded on</dt>
              <dd>{fmtDateTime(detail.upload_date)}</dd>
            </dl>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmBase}
        onOpenChange={(open) => !open && busyBaseId == null && setConfirmBase(null)}
        title={Number(confirmBase?.status) === 2 ? 'Resume this base?' : 'Pause this base?'}
        description={
          confirmBase && (
            <>
              <span className="font-medium text-foreground">{confirmBase.base_name}</span> (
              {Number(confirmBase.total_numbers || 0).toLocaleString('en-IN')} numbers)
              {Number(confirmBase.status) === 2
                ? ' will be dialled again when the campaign runs.'
                : ' will be skipped by the dialer until resumed.'}
            </>
          )
        }
        confirmLabel={Number(confirmBase?.status) === 2 ? 'Resume' : 'Pause'}
        loading={busyBaseId != null}
        onConfirm={() => confirmBase && toggleStatus(confirmBase)}
      />

      <ConfirmDialog
        open={!!deleteBase}
        onOpenChange={(open) => !open && busyBaseId == null && setDeleteBase(null)}
        title="Delete this base?"
        description={
          deleteBase && (
            <>
              <span className="font-medium text-foreground">{deleteBase.base_name}</span> (
              {Number(deleteBase.total_numbers || 0).toLocaleString('en-IN')} numbers) will be removed from this
              campaign and not dialled.
            </>
          )
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={busyBaseId != null}
        onConfirm={() => deleteBase && removeBase(deleteBase)}
      />

      {viewBaseId != null && (
        <NumbersViewerModal
          show
          onHide={() => setViewBaseId(null)}
          campaignId={String(campaignId)}
          campaignName={campaignName}
          expectedTotal={data?.total_nos}
          initialBaseId={viewBaseId}
        />
      )}

      {showAdd && (
        <AddBaseDialog
          campaignId={campaignId}
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false);
            load();
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}

function AddBaseDialog({
  campaignId,
  onClose,
  onAdded,
}: {
  campaignId: number | string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [method, setMethod] = useState<AddMethod>('manual');
  const [numbers, setNumbers] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileText, setFileText] = useState('');
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [groupId, setGroupId] = useState('');
  const [removeDuplicates, setRemoveDuplicates] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (method !== 'contact_group' || groups.length) return;
    getApiCall(API_CONTACT_GROUP_LIST, {})
      .then((res: ContactGroup[]) => setGroups(Array.isArray(res) ? res : []))
      .catch(() => setGroups([]));
  }, [method, groups.length]);

  // Live count before submit. Cross-base duplicates are only known to the server.
  const count = useMemo(() => {
    if (method === 'contact_group') {
      const g = groups.find((x) => String(x.id) === groupId);
      if (!g) return null;
      const n = Number(g.member_count) || 0;
      return { valid: n, stored: n, repeated: 0 };
    }
    if (method === 'upload_file' && !file) return null;
    const list = method === 'manual' ? manualNumbers(numbers) : fileNumbers(fileText);
    const unique = new Set(list).size;
    return { valid: list.length, stored: removeDuplicates ? unique : list.length, repeated: list.length - unique };
  }, [method, numbers, fileText, file, groups, groupId, removeDuplicates]);

  const submit = async () => {
    let contactSource: Record<string, unknown>;
    if (method === 'manual') {
      if (!numbers.trim()) return toast.error('Enter at least one number.');
      contactSource = { method, numbers };
    } else if (method === 'upload_file') {
      if (!file) return toast.error('Choose a CSV / TXT file.');
      contactSource = { method, lines: fileText };
    } else {
      if (!groupId) return toast.error('Select a contact group.');
      contactSource = { method, contact_group_id: Number(groupId) };
    }
    if (count && count.valid === 0) return toast.error('No valid 10-digit numbers found.');
    if (count && count.stored > BASE_NUMBER_CAP) {
      return toast.error(`A base can have at most ${BASE_NUMBER_CAP.toLocaleString('en-IN')} numbers.`);
    }
    setSaving(true);
    postApiCall(API_VOICE_CAMPAIGNS_BASES.replace(':id', String(campaignId)), {
      contact_source: contactSource,
      remove_duplicates: removeDuplicates ? 1 : 0,
    })
      .then(() => {
        toast.success('Base added');
        onAdded();
      })
      .catch(toastApiError('Unable to add base'))
      .finally(() => setSaving(false));
  };

  const selectClass =
    'h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add base</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="mb-1 block font-medium">Source</span>
            <select className={selectClass} value={method} onChange={(e) => setMethod(e.target.value as AddMethod)}>
              <option value="manual">Manual input</option>
              <option value="upload_file">Upload file</option>
              <option value="contact_group">Contact group</option>
            </select>
          </label>

          {method === 'manual' && (
            <label className="block">
              <span className="mb-1 block font-medium">Numbers (one 10-digit number per line, max 50,000)</span>
              <Textarea rows={7} value={numbers} onChange={(e) => setNumbers(e.target.value)} placeholder={'9876543210\n9123456789'} />
            </label>
          )}

          {method === 'upload_file' && (
            <>
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>&quot;Number&quot; header, then one 10-digit number per line. Extra columns are dropped.</span>
                <Button type="button" variant="outline" size="sm" onClick={downloadNumberTemplate}>
                  <Download className="h-3.5 w-3.5" />
                  Download Template
                </Button>
              </div>
              <label className="block">
                <span className="mb-1 block font-medium">File (CSV / TXT, number in first column, max 50,000)</span>
                <Input
                  type="file"
                  accept=".csv,.txt"
                  onChange={(e) => {
                    const picked = e.target.files?.[0] || null;
                    setFile(picked);
                    setFileText('');
                    picked
                      ?.text()
                      .then(setFileText)
                      .catch(() => toast.error('Unable to read the file.'));
                  }}
                />
              </label>
            </>
          )}

          {method === 'contact_group' && (
            <label className="block">
              <span className="mb-1 block font-medium">Contact group</span>
              <select className={selectClass} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                <option value="">Choose a contact group</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id} disabled={!Number(g.member_count)}>
                    {Number(g.member_count) ? g.group_name : `${g.group_name} (No contacts)`}
                  </option>
                ))}
              </select>
            </label>
          )}

          {count && (
            <div
              className={`rounded-md border px-3 py-2 text-xs ${
                count.stored > BASE_NUMBER_CAP || count.valid === 0
                  ? 'border-destructive/40 bg-destructive/10 text-destructive'
                  : 'border-border bg-muted/40 text-foreground'
              }`}
            >
              <div>
                Numbers to save:{' '}
                <span className="font-semibold">
                  {count.stored.toLocaleString('en-IN')} / {BASE_NUMBER_CAP.toLocaleString('en-IN')}
                </span>
              </div>
              {method !== 'contact_group' && (
                <div className="text-muted-foreground">
                  Valid: {count.valid.toLocaleString('en-IN')} · Repeated in this list: {count.repeated.toLocaleString('en-IN')}
                  {removeDuplicates && count.repeated > 0 ? ' (will be removed)' : ''}
                </div>
              )}
            </div>
          )}

          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={removeDuplicates}
              onChange={(e) => setRemoveDuplicates(e.target.checked)}
            />
            <span>
              Remove duplicates
              <span className="block text-xs text-muted-foreground">
                Drops numbers repeated inside this base only. Other bases are not compared.
              </span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? 'Adding…' : 'Add base'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
